import { FARM_DIAGNOSTICS_UNCROPPED_LABEL } from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import { InputTaskCategory } from 'interfaces/IInputTask';
import { getInputsForPaddock, getInputYear } from 'utils/FarmUtils';
import type { SummaryEntityCategory } from '../../../../redux/slices/InsightsSlice';
import {
  computeGrossMarginForInputs,
  type GrossMarginResult,
} from '../Insights/computeGrossMargin';
import { STEP_TYPE_TO_SUMMARY_ENTITY } from '../Insights/summary2/summary2Utils';

/** Mid/"Expected" cell of GrossMarginResult's 3x3 [yieldScenario][priceScenario] matrix — matches AnalysisView's own default scenario selection. */
const EXPECTED_SCENARIO = 1;

export interface WueTrendCategoryResult {
  areaHa: number;
  yieldValue: number;
  yieldUnit: string;
  /** $ per yieldUnit (e.g. $/t) at the "Expected" price scenario. */
  pricePerUnit: number;
  production: number;
  incomeTotal: number;
  costsTotal: Partial<Record<SummaryEntityCategory, number>>;
  totalCostsTotal: number;
  grossMarginTotal: number;
  /** Total costs ÷ income. Null when there's no income to divide by. */
  costPercentIncome: number | null;
}

export interface WueTrendYearRow {
  year: number;
  /** Which category has data by default — Actual when this year has any, else Plan. Callers may still read the other field directly to override per row. */
  defaultCategory: InputTaskCategory;
  actual: WueTrendCategoryResult | null;
  plan: WueTrendCategoryResult | null;
  /**
   * Crop name(s) planted that year — e.g. "Wheat", or "Wheat, Canola" for a
   * double-cropped paddock-year, or "Uncropped" when no crop name was set.
   * Row-level (not per Plan/Actual): crop identity is a property of the
   * underlying Input document, unaffected by which task category is shown.
   */
  crop: string;
}

const reshapeCategoryResult = (
  gm: GrossMarginResult
): WueTrendCategoryResult | null => {
  if (!gm.hasData) return null;

  const costsTotal: Partial<Record<SummaryEntityCategory, number>> = {};
  for (const line of gm.costBreakdown) {
    const category = STEP_TYPE_TO_SUMMARY_ENTITY[line.stepType];
    if (!category) continue;
    costsTotal[category] = (costsTotal[category] ?? 0) + line.totalCost;
  }

  const incomeTotal = gm.revenue[EXPECTED_SCENARIO][EXPECTED_SCENARIO];
  const grossMarginTotal = gm.gm[EXPECTED_SCENARIO][EXPECTED_SCENARIO];
  const totalCostsTotal = Math.abs(gm.tvcTotal);

  return {
    areaHa: gm.refArea,
    yieldValue: gm.refYields[EXPECTED_SCENARIO],
    yieldUnit: gm.yieldUnit,
    pricePerUnit: gm.refPrices[EXPECTED_SCENARIO],
    production: gm.refYields[EXPECTED_SCENARIO] * gm.refArea,
    incomeTotal,
    costsTotal,
    totalCostsTotal,
    grossMarginTotal,
    costPercentIncome: incomeTotal > 0 ? totalCostsTotal / incomeTotal : null,
  };
};

/**
 * One row per crop-year for a single paddock — the multi-year inverse of
 * Analysis's one-row-per-paddock-plan. Both Plan and Actual are computed for
 * every year up front (rather than picking one category) so a "Farm
 * Diagnostics" table can default each row to Actual (falling back to Plan)
 * while still letting the user flip an individual year to the other source.
 *
 * A year with neither Plan nor Actual data produces no row at all — callers
 * must not backfill a missing year with zeros.
 */
export const computeWueTrend = (
  inputs: IInput[],
  paddockId: string,
  gUnits: GUnit[]
): WueTrendYearRow[] => {
  const paddockInputs = getInputsForPaddock(inputs, paddockId);

  const inputsByYear = new Map<number, IInput[]>();
  for (const input of paddockInputs) {
    const year = getInputYear(input);
    if (year === null) continue;
    const existing = inputsByYear.get(year);
    if (existing) {
      existing.push(input);
    } else {
      inputsByYear.set(year, [input]);
    }
  }

  const rows: WueTrendYearRow[] = [];
  for (const [year, yearInputs] of Array.from(inputsByYear.entries())) {
    const actual = reshapeCategoryResult(
      computeGrossMarginForInputs(
        yearInputs,
        new Set([InputTaskCategory.Actual]),
        gUnits
      )
    );
    const plan = reshapeCategoryResult(
      computeGrossMarginForInputs(
        yearInputs,
        new Set([InputTaskCategory.Plan]),
        gUnits
      )
    );
    if (!actual && !plan) continue;

    const cropNames = Array.from(
      new Set(
        yearInputs
          .map((input) => input.crop?.cropName?.trim())
          .filter((name): name is string => !!name)
      )
    );

    rows.push({
      year,
      defaultCategory: actual ? InputTaskCategory.Actual : InputTaskCategory.Plan,
      actual,
      plan,
      crop: cropNames.length > 0 ? cropNames.join(', ') : FARM_DIAGNOSTICS_UNCROPPED_LABEL,
    });
  }

  return rows.sort((a, b) => a.year - b.year);
};
